/**
 * Translatable String Utility
 *
 * Supports multi-language fields that can be either a plain string (legacy)
 * or a Record<string, string> with language codes as keys.
 */

export type TranslatableString = string | Record<string, string>;

export const SUPPORTED_LANGUAGES = ['en', 'fr', 'ar', 'es'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

/**
 * Resolve a translatable value to a plain string for the given language.
 * Handles both legacy plain strings and new multi-language objects.
 *
 * Falls back through: exact lang → base lang (e.g. 'fr' from 'fr-FR') → fallback → first value.
 * Empty strings are skipped during fallback (only non-empty values are considered).
 *
 * @param value - The translatable value (string, Record, null, or undefined)
 * @param lang - Desired language code (supports locale codes like 'fr-FR')
 * @param fallback - Fallback language code (defaults to 'en')
 * @returns The resolved string, or '' if no value is available
 */
export function resolveTranslation(
  value: TranslatableString | null | undefined,
  lang: string,
  fallback = 'en',
): string {
  if (!value) return '';
  if (typeof value === 'string') return value;

  // Extract base language from locale code (e.g. 'fr-FR' → 'fr')
  const baseLang = lang.includes('-') ? lang.split('-')[0] : lang;
  const baseFallback = fallback.includes('-') ? fallback.split('-')[0] : fallback;

  // Try exact lang, base lang, exact fallback, base fallback, then first non-empty value
  return value[lang]
    || (baseLang !== lang ? value[baseLang] : '')
    || value[fallback]
    || (baseFallback !== fallback ? value[baseFallback] : '')
    || Object.values(value).find(v => !!v)
    || '';
}
